/**
 * Tutorial steps used by the `FeatureTour` overlay in the property editor.
 * Kept out of the components so:
 *   - copy is easy to update without touching JSX
 *   - i18n hook-in is simpler when we localize the tours
 */
import { Sparkles, Tag, Calendar, Users, Zap, RefreshCw, Lock, Moon, DollarSign, Link2, BookOpen, Compass, Home, Key, Palette, Languages, Bus, Smartphone, Bell, Image as ImageIcon, Send, Globe, ShieldCheck } from "lucide-react";

// ═════════════════════════════════════════════════
// PRICING — full walkthrough of the pricing panel
// ═════════════════════════════════════════════════
export const PRICING_TOUR_STEPS = [
  {
    icon: Sparkles,
    color: "#059669", // emerald-600
    title: "Prezzi trasparenti in 3 minuti ✨",
    text: "Ti mostro come impostare prezzi, spese di pulizia, imposta di soggiorno e regole speciali. Tutto FACOLTATIVO — se lasci vuoto, in landing pubblica non compare nulla.",
    bullets: [
      "Prezzo base + persone di riferimento",
      "Costi extra: pulizia e imposta di soggiorno",
      "Weekend / mese / festività con maggiorazione",
    ],
  },
  {
    selector: '[data-testid="pricing-enabled-toggle"]',
    icon: Tag,
    color: "#059669",
    title: "1 · Attiva la sezione prezzi",
    text: "Se non ti serve un prezzo esposto in landing, lascialo spento e non apparirà nulla di pubblico. Attivalo per mostrare tariffe indicative agli ospiti che visitano il tuo sito.",
  },
  {
    selector: '[data-testid="pricing-base-price"]',
    icon: DollarSign,
    color: "#059669",
    title: "2 · Prezzo base a notte",
    text: "È il tuo tariffa \"a partire da\" — quello che gli ospiti vedono in vetrina. Consigliamo il prezzo di bassa stagione con occupazione minima: le maggiorazioni si aggiungono dopo con le regole.",
  },
  {
    selector: '[data-testid="pricing-base-guests"]',
    icon: Users,
    color: "#059669",
    title: "3 · A quante persone si riferisce",
    text: "Se il prezzo copre 2 persone, in landing apparirà: \"Il prezzo base è a partire da €X, riferito a 2 persone\". Se lasci vuoto, mostriamo un disclaimer più generico.",
  },
  {
    selector: '[data-testid="pricing-cleaning-fee"], [data-testid="pricing-tourist-tax"]',
    icon: Sparkles,
    color: "#059669",
    title: "4 · Costi aggiuntivi (facoltativi)",
    text: "Se compili spese di pulizia o imposta di soggiorno, appaiono in un box separato nella landing pubblica. Se lasci vuoti entrambi, il box NON viene mostrato — massima trasparenza.",
    bullets: [
      "Pulizia = una tantum (€45, €60, …)",
      "Imposta di soggiorno = per persona per notte (€2/pers/notte)",
    ],
  },
  {
    selector: '[data-testid="pricing-min-nights"]',
    icon: Moon,
    color: "#059669",
    title: "5 · Soggiorno minimo",
    text: "Se richiedi almeno 2, 3, 7 notti (come nei periodi di picco), imposta qui il valore. Se vuoto, in landing non appare. Se compilato, viene mostrato accanto al prezzo come badge.",
  },
  {
    selector: '[data-testid="pricing-weekend-toggle"], [data-testid="pricing-monthly-toggle"]',
    icon: Zap,
    color: "#059669",
    title: "6 · Regole automatiche (opzionali)",
    text: "Weekend con +% di maggiorazione. Modificatori per singolo mese (bassa/alta stagione). Il calendario e la landing calcolano tutto in automatico usando queste regole.",
    bullets: [
      "Weekend: +20% (sabato + domenica)",
      "Mese: -30% in gennaio, +50% in agosto…",
    ],
  },
  {
    icon: Calendar,
    color: "#059669",
    title: "7 · Prezzi puntuali dal calendario",
    text: "Vuoi cambiare il prezzo SOLO per Natale o Ferragosto? Vai nella pagina Calendario → clicca la data (o trascina un intervallo) → imposta il prezzo assoluto. Le festività italiane hanno cornice rossa e un pulsante \"Prezzo festivo suggerito\".",
  },
];

// ═════════════════════════════════════════════════
// WELCOME BOOK EDITOR — how to fill it end-to-end
// ═════════════════════════════════════════════════
export const WELCOMEBOOK_TOUR_STEPS = [
  {
    icon: BookOpen,
    color: "#F59E0B", // amber-500 — matches the Guestbook brand
    title: "Welcome Book Digitale in 5 minuti 📖",
    text: "Ti accompagno tra i passi principali del wizard. Ogni sezione è indipendente: compila SOLO quelle che ti servono e salva. Le traduzioni EN/FR/ES/DE partono automaticamente in background.",
    bullets: [
      "Check-in, Manuali, Luoghi, Regole — tutto in un wizard",
      "Auto-traduzione al salvataggio (Gemini via Emergent LLM)",
      "Il tuo ospite lo apre da un QR code — offline-friendly",
    ],
  },
  {
    selector: '[data-testid="step-brand"]',
    icon: Palette,
    color: "#F59E0B",
    title: "1 · Brand & Benvenuto",
    text: "Il primo passo: logo, colore accento, testo di benvenuto e video introduttivo (opzionale). È la prima cosa che il tuo ospite vede quando apre il libretto — falla accogliente.",
    bullets: [
      "Testo di benvenuto in italiano — le traduzioni sono automatiche",
      "Colore accento coerente col tuo brand",
      "Link tip/mancia e recensione Google (facoltativi)",
    ],
  },
  {
    selector: '[data-testid="step-checkin"]',
    icon: Key,
    color: "#EF4444", // red-500 — check-in section is red in the editor
    title: "2 · Check-in",
    text: "Il cuore dell'esperienza: come arrivare, dove parcheggiare, come entrare in autonomia. Se hai un pre-check-in Chekin, incolla qui il link. Le indicazioni supportano markdown (grassetto, elenchi, link).",
    bullets: [
      "Indicazioni stradali + link Google Maps",
      "Parcheggi con distanza in minuti e link mappa",
      "Istruzioni self check-in con codici / cassaforte",
      "Note extra (accoglienza personalizzata, orari)",
    ],
  },
  {
    selector: '[data-testid="step-manuals"]',
    icon: BookOpen,
    color: "#8B5CF6", // violet-500
    title: "3 · Manuali (Wi-Fi, riscaldamento, elettrodomestici)",
    text: "Aggiungi tutte le guide che vuoi: Wi-Fi, riscaldamento, TV, lavatrice, aria condizionata… Ogni manuale può avere titolo, descrizione, contenuto completo e FAQ dedicate.",
    bullets: [
      "Titolo + descrizione breve (mostrata nell'anteprima)",
      "Contenuto completo con markdown supportato",
      "FAQ singole con domanda + risposta",
      "L'ordine può essere cambiato con drag & drop",
    ],
  },
  {
    selector: '[data-testid="step-concierge"]',
    icon: Compass,
    color: "#0EA5E9", // sky-500
    title: "4 · Luoghi consigliati (Concierge)",
    text: "Il tuo tocco locale: consigli i tuoi posti del cuore. Ristoranti, bar, spiagge, panorami, negozi. Per ogni luogo scrivi PERCHÉ vale la pena andarci — è quello che rende un host memorabile.",
    bullets: [
      "Categoria (mangiare, bere, spiaggia, cultura…)",
      "Nome, indirizzo, distanza",
      "\"Perché andare\" personale (max 300 caratteri)",
      "Google Maps link per apertura diretta",
    ],
  },
  {
    selector: '[data-testid="step-rules"]',
    icon: Home,
    color: "#DC2626", // red-600
    title: "5 · Regole della casa",
    text: "Trasparenza totale: orari check-in/out, chiavi, imposta di soggiorno, regole custom. Se hai regole particolari (no feste, no fumo, animali sì/no) aggiungile qui — l'ospite le legge PRIMA di arrivare.",
    bullets: [
      "Istruzioni chiavi (dove lasciarle a fine soggiorno)",
      "Info imposta di soggiorno",
      "Regole custom illimitate (una per riga)",
    ],
  },
  {
    selector: '[data-testid="translate-all-btn"]',
    icon: Languages,
    color: "#0EA5E9",
    title: "6 · Traduzioni automatiche",
    text: "Ogni volta che salvi, se il testo è cambiato, Gemini traduce SOLO i campi modificati in EN/FR/ES/DE in background. Cliccando qui puoi forzare una nuova traduzione manualmente.",
    bullets: [
      "Traduzione incrementale (solo diff → risparmio credit)",
      "Cache in DB: apertura pubblica del Welcome Book è istantanea",
      "4 lingue in parallelo — tempo totale ~15s",
    ],
  },
  {
    selector: '[data-testid="step-updates"]',
    icon: Zap,
    color: "#F59E0B",
    title: "7 · Aggiornamenti Live",
    text: "Novità in tempo reale per gli ospiti già arrivati: 'Il custode è tornato', 'Chiusura acqua', 'Consiglio del giorno'. Vengono mostrati in cima al Welcome Book e (se hai la Push) notificati.",
  },
  {
    selector: '[data-testid="step-transports"]',
    icon: Bus,
    color: "#0EA5E9", // sky-500
    title: "8 · Trasporti locali (come muoversi)",
    text: "Aiuti l'ospite a orientarsi con mezzi pubblici, car/bike sharing, taxi, NCC e collegamenti aeroporto. Ogni categoria ha una forma diversa: elenco singolo, schede per operatore, sotto-voci per mezzo. Attiva SOLO quelle che ti servono con il toggle a destra.",
    bullets: [
      "Trasporti pubblici → elenco: es. \"ATAC bus 780\", \"Metro B — fermata Piramide\"",
      "Car sharing → una scheda per operatore: es. \"Free2Move\", \"Enjoy\"",
      "Bike sharing → una scheda per app: es. \"Lime\", \"Dott\"",
      "Aeroporto → sotto-voci per mezzo (Bus/Treno/Taxi/NCC): es. \"Leonardo Express\" sotto Treno",
      "Ogni voce ha nome, telefono, sito, link App Store / Google Play",
    ],
  },
  {
    selector: '[data-testid="save-guestbook-btn"]',
    icon: Sparkles,
    color: "#F59E0B",
    title: "9 · Salva e via!",
    text: "Clicca \"Salva\" alla fine di ogni sezione — le modifiche vanno in cache immediatamente. Poi stampa il QR code dalla dashboard e mettilo in casa: pronto in 5 minuti.",
  },
];

// ═════════════════════════════════════════════════
// iCal — sync with Airbnb/Booking/VRBO
// ═════════════════════════════════════════════════
export const ICAL_TOUR_STEPS = [
  {
    icon: RefreshCw,
    color: "#0EA5E9", // sky-500
    title: "Zero doppie prenotazioni 🎯",
    text: "Collegando i link iCal di Airbnb, Booking e VRBO al tuo calendario Hostland, le date prenotate su ogni piattaforma vengono bloccate in automatico ogni 30 minuti.",
    bullets: [
      "Sync ogni 30 minuti in background",
      "Ogni fonte è colorata diversamente",
      "I blocchi manuali RIMANGONO se rimuovi un URL",
    ],
  },
  {
    icon: Link2,
    color: "#FF5A5F",
    title: "1 · Airbnb → Esporta iCal",
    text: "Dal tuo host account Airbnb vai su: Calendario → Disponibilità → Connetti calendario → Esporta calendario. Copia il link .ics che ottieni.",
    bullets: [
      "Airbnb → Menù host → Calendario",
      "Sezione Disponibilità → Connetti",
      "Copia l'URL Exports (.ics)",
    ],
  },
  {
    icon: Link2,
    color: "#003580",
    title: "2 · Booking.com → Sync calendari",
    text: "Da Booking.com Extranet vai su: Tariffe & Disponibilità → Calendario → Sincronizza calendari → Esporta calendario. Copia il link .ics.",
    bullets: [
      "Extranet Booking → Tariffe & Disponibilità",
      "Sincronizza calendari → Esporta",
      "Copia l'URL .ics generato",
    ],
  },
  {
    icon: Link2,
    color: "#245ABC",
    title: "3 · VRBO → Export",
    text: "Da VRBO/HomeAway vai su: Calendario → Sincronizzazione → Esporta calendario. Copia il link e incolla nel campo VRBO qui.",
  },
  {
    selector: '[data-testid="ical-input-airbnb"], [data-testid="ical-input-booking"], [data-testid="ical-input-vrbo"]',
    icon: RefreshCw,
    color: "#0EA5E9",
    title: "4 · Incolla gli URL",
    text: "Incolla ciascun URL nel campo corrispondente. Puoi collegare 1, 2 o tutti e 3 i canali. Ogni URL è indipendente: aggiungi/rimuovi in qualsiasi momento.",
  },
  {
    selector: '[data-testid="ical-save-btn"]',
    icon: Zap,
    color: "#0EA5E9",
    title: "5 · Salva URL iCal",
    text: "Clicca \"Salva URL iCal\" per registrare i link. Da questo momento in poi Hostland riscarica il calendario ogni 30 minuti in automatico.",
  },
  {
    selector: '[data-testid="ical-sync-btn"]',
    icon: RefreshCw,
    color: "#10B981",
    title: "6 · Sincronizza ora",
    text: "Vuoi vedere subito i risultati? Clicca \"Sincronizza ora\": le date prenotate su Airbnb/Booking/VRBO vengono bloccate immediatamente nel tuo calendario Hostland con il colore del canale corrispondente.",
    bullets: [
      "Airbnb → rosso corallo",
      "Booking → blu scuro",
      "VRBO → azzurro",
      "Chiuso manualmente → nero",
    ],
  },
  {
    selector: '[data-testid="cal-action-bar"], [data-testid="cal-help"]',
    icon: Lock,
    color: "#8B5CF6",
    title: "7 · Blocca / prezzi manuali",
    text: "Tocca una data → apri il pannello per prezzo/chiusura. Trascina più celle → gestisci in blocco. Le festività italiane hanno cornice rossa con un pulsante \"prezzo suggerito alto\" per non perdere ricavi nei picchi.",
  },
];


// ═════════════════════════════════════════════════
// PWA + PUSH — how to turn the property into a phone app
// ═════════════════════════════════════════════════
export const PWA_PUSH_TOUR_STEPS = [
  {
    icon: Smartphone,
    color: "#7C3AED",
    title: "Trasforma la tua struttura in un'app 📱",
    text: "Con un tap gli ospiti installano la tua landing come un'app sul telefono — icona, nome e colori personalizzabili. Da qui potrai poi mandargli notifiche push con sconti, novità e promozioni riservate.",
    bullets: [
      "Nessun App Store, nessun download da 80 MB",
      "Un solo tap: la struttura diventa un'icona sulla Home",
      "Notifiche push targettizzate solo ai TUOI ospiti",
    ],
  },
  {
    selector: '[data-testid="pwa-section"]',
    icon: ImageIcon,
    color: "#7C3AED",
    title: "1 · Personalizza l'app installabile",
    text: "Scegli COSA vedrà l'ospite sul suo telefono: icona, nome breve e colore tema. Se non compili nulla usiamo il logo Hostland come fallback.",
    bullets: [
      "Icona: qualsiasi immagine quadrata PNG/JPG/WebP (max 5MB)",
      "Se non è quadrata la ritaglio io al centro, 512×512 + 192×192",
      "Nome corto: max 12 caratteri (è quello che appare sotto l'icona)",
      "Colore tema: dà il tocco alla barra del browser",
    ],
  },
  {
    selector: '[data-testid="pwa-icon-upload-btn"]',
    icon: ImageIcon,
    color: "#7C3AED",
    title: "2 · Carica la tua icona",
    text: "Consiglio: usa il tuo logo su fondo pieno (non trasparente). L'icona sarà minuscola sul telefono — testo lungo diventa illeggibile, meglio un simbolo forte o una H stilizzata.",
  },
  {
    selector: '[data-testid="pwa-save-btn"]',
    icon: ShieldCheck,
    color: "#7C3AED",
    title: "3 · Salva e testa dal vivo",
    text: "Salva e apri la landing della property (link Preview live in alto). Su mobile Chrome/Android o Safari/iOS vedrai il banner \"Installa\" spuntare dal basso — provalo con il tuo telefono prima di condividerlo con gli ospiti!",
  },
  {
    selector: '[data-testid="push-audience-card"]',
    icon: Users,
    color: "#059669",
    title: "4 · Chi riceverà le tue notifiche",
    text: "Ogni ospite che installa la webapp e accetta le notifiche entra automaticamente nel tuo pubblico. Il contatore mostra a quante persone stai scrivendo — l'invio è filtrato solo su questa property, MAI condiviso con altri host.",
  },
  {
    selector: '[data-testid="push-tpl-promo"], [data-testid="push-tpl-return"]',
    icon: Sparkles,
    color: "#F59E0B",
    title: "5 · Parti da un template",
    text: "Quattro modelli pronti per situazioni tipiche: promozione stagionale, sconto ritorno, nuovo servizio, aggiornamento. Cliccali per popolare titolo e messaggio, poi personalizzali con il tuo tono di voce.",
  },
  {
    selector: '[data-testid="push-url-input"]',
    icon: Link2,
    color: "#F59E0B",
    title: "6 · Deep link (facoltativo ma potente)",
    text: "Se aggiungi un link, quando l'ospite tocca la notifica si apre esattamente lì. Usalo per: pagina promo, prenotazione diretta, blog post, offerta lampo. Se lasci vuoto si apre la landing della struttura.",
  },
  {
    selector: '[data-testid="push-send-btn"]',
    icon: Send,
    color: "#FF385C",
    title: "7 · Invia in un tap",
    text: "L'invio è istantaneo: OneSignal recapita la push in pochi secondi a chi ha il telefono acceso, e la mette in coda per gli offline. Dopo l'invio trovi lo storico in fondo alla pagina — vedi quante persone hanno davvero ricevuto la notifica.",
  },
  {
    icon: Globe,
    color: "#3B82F6",
    title: "⚠️ Limitazioni browser da conoscere",
    text: "Le WebApp e le notifiche push funzionano SOLO su alcuni browser — per gli altri il banner non appare (meglio così, evitiamo confusione).",
    bullets: [
      "📱 iPhone/iPad → SOLO Safari (Chrome iOS non le supporta)",
      "📱 Android → SOLO Chrome (Firefox/Samsung Internet no)",
      "💻 Mac → Safari (Add to Dock) o Chrome",
      "💻 Windows/Linux → Chrome ed Edge",
      "Su desktop macOS servono Safari 17+ / macOS Sonoma o superiore",
    ],
  },
  {
    icon: Bell,
    color: "#059669",
    title: "Ultimo consiglio: qualità > quantità",
    text: "Le notifiche funzionano se le usi con parsimonia. Consiglio: 1-2 push al mese massimo — solo se hai davvero qualcosa di utile da dire (uno sconto vero, una novità nella struttura). Altrimenti gli ospiti disattivano tutto e perdi il canale per sempre.",
  },
];
